import { Capacitor, registerPlugin } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { hasNativePlugin } from './NativeBiometricAuthService'
import { getNativeToken } from './Firebase/native'
import registerNotificationToken from './NotificationTokenApi'

// Whether the user wants push on this device. Held in native Preferences
// (UserDefaults / SharedPreferences) rather than localStorage so it is owned by
// the app: the WebView can evict site storage under pressure, which would
// silently switch notifications back on for someone who turned them off.
const ENABLED_KEY = 'push_notifications_enabled'

// Absent means "never touched the switch", and the app has always registered a
// token on launch, so the honest default is on.
const DEFAULT_ENABLED = true

export const PERMISSION = {
  granted: 'granted',
  denied: 'denied',
  prompt: 'prompt',
}

// Both plugins this module uses must actually be in the binary. Older shells are
// native but carry neither, and calling into a missing plugin is what hung the
// app on the login screen (see nativePlugins.js).
const isSupported = () => Capacitor.isNativePlatform() &&
  hasNativePlugin('FirebaseMessaging') && hasNativePlugin('Preferences')

// FCM calls talk to Google's servers, so they can hang for a long time on a
// slow or offline device - long enough to look frozen. Everything here is
// time-boxed: the switch reflects what the user asked for, and a token that
// could not be fetched or deleted is reconciled on the next launch instead of
// blocking the screen.
const CALL_TIMEOUT_MS = 8000

const withTimeout = (promise, ms = CALL_TIMEOUT_MS) => Promise.race([
  promise,
  new Promise((resolve) => { setTimeout(() => resolve({ timedOut: true }), ms) }),
])

/**
 * The user's own preference, independent of the OS permission.
 *
 * Both have to be true for notifications to arrive, and they are deliberately
 * kept apart: revoking the OS permission must not silently rewrite what the
 * user asked for in the app, or turning permission back on in device settings
 * would land them somewhere they never chose.
 */
export const isPushEnabled = async () => {
  if (!isSupported()) return false
  try {
    const { value } = await Preferences.get({ key: ENABLED_KEY })
    if (value === null || value === undefined) return DEFAULT_ENABLED
    return value === 'true'
  } catch (error) {
    return DEFAULT_ENABLED
  }
}

const setStoredPreference = async (enabled) => {
  try {
    await Preferences.set({ key: ENABLED_KEY, value: enabled ? 'true' : 'false' })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to persist push notification preference', error)
  }
}

export const getPermissionStatus = async () => {
  if (!isSupported()) return PERMISSION.denied
  try {
    const { receive } = await FirebaseMessaging.checkPermissions()
    return receive || PERMISSION.prompt
  } catch (error) {
    return PERMISSION.prompt
  }
}

/**
 * Turns push on for this device.
 *
 * Asks the OS for permission when it has not been decided yet, then registers a
 * fresh token. Returns `blocked: true` when the OS refuses - the app cannot
 * re-ask once permission was denied, so the caller sends the user to device
 * settings instead of pretending it worked.
 */
// Fetches a token and hands it to the backend without anyone waiting on it.
// Failure is safe: the preference is already stored, so the next launch (or the
// plugin's own token-refresh event) registers it.
const registerTokenInBackground = () => {
  withTimeout(getNativeToken())
    .then((token) => (typeof token === 'string' && token
      ? withTimeout(registerNotificationToken(token))
      : null))
    .catch(() => {})
}

export const enablePush = async () => {
  if (!isSupported()) return { enabled: false, blocked: false }

  let permission = await getPermissionStatus()

  // Only ever wait on the OS prompt, and only when permission has not been
  // decided yet - that pause is the user's own, and they can see it. Everything
  // after it is network work they should not have to watch.
  if (permission === PERMISSION.prompt) {
    try {
      const asked = await withTimeout(FirebaseMessaging.requestPermissions())
      permission = asked?.receive || await getPermissionStatus()
    } catch (error) {
      permission = await getPermissionStatus()
    }
  }

  // The switch records what the user wants from Orgarise, which is a separate
  // question from whether the OS currently lets anything through. So a denied
  // permission still turns the preference on - the app is ready, and the popup
  // sends them to device settings to lift the block. Nothing arrives until they
  // do, but the moment they do, no second visit here is needed.
  await setStoredPreference(true)
  registerTokenInBackground()

  return { enabled: true, blocked: permission === PERMISSION.denied }
}

/**
 * Opens the Orgarise notification page in the device's own settings.
 *
 * Bound through registerPlugin so the call compiles and ships today; it starts
 * working the moment a native settings plugin is installed in the shell, with
 * no change here. Until then the caller just closes its dialog.
 */
export const openDeviceSettings = async () => {
  if (!isSupported()) return false

  // registerPlugin() succeeds for a name the binary does not implement, and the
  // call that follows is what would hang rather than fail. Checking the bridge
  // first keeps that impossible.
  if (!hasNativePlugin('NativeSettings')) return false

  try {
    const NativeSettings = registerPlugin('NativeSettings')
    await NativeSettings.openAndroid({ option: 'application_details' })
      .catch(() => NativeSettings.openIOS({ option: 'app' }))
    return true
  } catch (error) {
    return false
  }
}

/**
 * Turns push off for this device.
 *
 * Deleting the FCM token stops delivery at the source, so nothing arrives from
 * any module - they all publish to this one device token - and it takes effect
 * without waiting for a backend round trip. The preference is stored alongside
 * it so the app does not simply mint a new token on the next launch.
 */
export const disablePush = async () => {
  if (!isSupported()) return

  // The preference is what the app acts on, and writing it is instant, so it
  // goes first and the switch can flip immediately.
  await setStoredPreference(false)

  // Deleting the token stops delivery at the source, but it is a network call
  // that can hang. It is not load-bearing: with the preference off, nothing
  // re-registers a token on the next launch either way.
  withTimeout(FirebaseMessaging.deleteToken()).catch(() => {})
}

export default {
  isPushEnabled, enablePush, disablePush, getPermissionStatus, PERMISSION,
}
